/**
 * The left navigation (docs/07-gui-proposal §2, rebuilt full-height on 12/09/2026 —
 * docs/13-tasks/01-ui-chrome.md#18). It is now the whole left column of the shell, from the top of the window to
 * the bottom, and it carries the three things that answer "where am I":
 *
 *   1. the mark and the name of the system — one button, back to the overview;
 *   2. the screens, in five groups, each item an icon + a label;
 *   3. who is signed in, and the way out.
 *
 * The top bar keeps only what belongs to the PAGE (period, freshness, theme) — identity and
 * navigation left it, because those answer a different question and were crowding the controls a
 * reader touches on every screen.
 *
 * Collapsed state (`rail`) is the shell's, not this file's: the grid column width lives in CSS
 * and is keyed off `data-nav` on `.app`, so nothing here computes a width. What this file does
 * change when collapsed is the TEXT: a rail with no labels has to put every label into `title`
 * and `aria-label`, or it is unusable by mouse and unreadable by a screen reader alike.
 *
 * ≤ 900 px the same list becomes a drawer (docs/13-tasks/01-ui-chrome.md#16 M1) and is never a rail — a drawer that
 * costs a tap to open must show its labels. `open` only writes `data-open`; every measurement of
 * the drawer is in `responsive.css`. The nav stays in the DOM when closed (`visibility: hidden`,
 * not unmounted) so its scroll position and the browser's in-page search keep working. Picking an
 * item calls `onPick`, which is how the drawer closes — on the wide screen that call changes
 * nothing, so there is one code path.
 *
 * The drawer carries its OWN close button. The ☰ of the top bar cannot double as the close: the
 * drawer lies over the top bar, so that button is behind it and untappable (measured 12/09/2026).
 * Three ways out then, all working: this button, the scrim, and Escape.
 */
import { NavLink } from 'react-router';

import { visibleSections } from '../navigation.js';
import type { SessionUser } from '../services/authApi.js';
import { navRailActionLabel } from '../utils/navRail.js';
import { AccountMenu } from './AccountMenu.js';
import { ChevronIcon, NavIcon } from './NavIcons.js';

export function SideNav({
  user,
  rail,
  open,
  onToggleRail,
  onPick,
}: {
  user: SessionUser;
  /** Collapsed to the icon rail — ignored ≤ 900 px, where the nav is a drawer. */
  rail: boolean;
  open: boolean;
  onToggleRail: () => void;
  onPick: () => void;
}) {
  return (
    <nav id="mainnav" className="side" data-open={open} aria-label="Điều hướng">
      <div className="navhead">
        {/*
          Mark + name are ONE button, not two targets side by side: a reader aims at "the logo"
          and does not know whether the pointer landed on the picture or on the words.
          `alt=""` because the name is right there — reading it twice is noise.
        */}
        <NavLink to="/" end className="navbrand" onClick={onPick} title="Về trang Tổng quan">
          <img src="/icons/icon-192.png" alt="" width="34" height="34" className="brandmark" />
          <span className="brand">
            <b>Thống kê tàu</b>
          </span>
        </NavLink>

        {/* Chỉ có ở khổ rộng: ở khổ ngăn kéo, thu gọn thành dải icon là vô nghĩa. */}
        <button
          type="button"
          className="navrailbtn touch"
          aria-expanded={!rail}
          aria-controls="mainnav"
          aria-label={navRailActionLabel(rail)}
          title={navRailActionLabel(rail)}
          onClick={onToggleRail}
        >
          <ChevronIcon direction={rail ? 'right' : 'left'} />
        </button>

        <button type="button" className="navclose touch" onClick={onPick}>
          <span aria-hidden="true">✕</span> Đóng
        </button>
      </div>

      <div className="navscroll">
        {visibleSections(user.role).map((section) => (
          <div className="navsection" key={section.title}>
            {/*
              Đầu mục có icon riêng, và ở dải thu gọn nó THAY cho chữ: mất chữ mà không còn dấu
              hiệu nào thì mười lăm icon dính thành một cột không đầu không cuối.
            */}
            <p className="navgroup">
              <NavIcon name={section.icon} size={14} />
              <span>{section.title}</span>
            </p>
            <ul>
              {section.items.map((item) => (
                <li key={item.path}>
                  {item.ready ? (
                    <NavLink
                      to={item.path}
                      end={item.path === '/'}
                      className="navitem"
                      /* Tooltip CHỈ khi đã thu gọn: khi còn nhãn, nó chỉ đọc lại chữ đang thấy. */
                      title={rail ? item.label : undefined}
                      onClick={onPick}
                    >
                      <NavIcon name={item.icon} />
                      <span className="navlabel">{item.label}</span>
                    </NavLink>
                  ) : (
                    <span
                      className="navitem"
                      title={rail ? `${item.label} — đang dựng` : undefined}
                    >
                      <NavIcon name={item.icon} />
                      <span className="navlabel">{item.label}</span>
                      <em className="stubtag not-italic">đang dựng</em>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="navfoot">
        <AccountMenu user={user} rail={rail} />
      </div>
    </nav>
  );
}
